import axios from 'axios';
import { toast } from 'sonner';
import { getToken, clearAuth, isTokenExpired } from './auth';

// Throttle gate for the 429 "too many requests" toast (avoids toast spam).
let _last429ToastAt = 0;

/**
 * API Client for SAFER-i™
 * Handles authentication, error handling, and token management
 */

// Determine the correct backend URL
const getBackendUrl = () => {
  if (typeof window === 'undefined') {
    return process.env.REACT_APP_BACKEND_URL || '';
  }
  
  // Check for global variable set by index.html
  if (window.__SAFER_BACKEND_URL__) {
    return window.__SAFER_BACKEND_URL__;
  }
  
  const origin = window.location.origin;
  
  // Production domains
  if (origin.includes('.emergent.host') || 
      origin.includes('.emergent.sh') ||
      origin.includes('safer-i.com') ||
      origin.includes('.preview.emergentagent.com')) {
    return origin;
  }
  
  // Development fallback
  return process.env.REACT_APP_BACKEND_URL || origin;
};

const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;


// Create axios instance
const api = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,  // Important: Send cookies for OAuth sessions
  timeout: 30000,  // 30 second timeout
});

// Request interceptor - adds auth token to all requests
api.interceptors.request.use(
  (config) => {
    // Get token from unified auth utility
    const token = getToken();
    
    if (token) {
      // Check if token might be expired
      if (isTokenExpired()) {
        console.warn('[SAFER-i™] Token may be expired, request may fail');
      }
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - handles errors globally
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    
    // Handle authentication errors
    if (status === 401) {
      // Silent requests (e.g. telemetry from public pages) must not force a
      // redirect — they're best-effort signals, not user actions.
      if (error.config?.skipAuthRedirect) {
        return Promise.reject(error);
      }
      // Clear all auth data
      clearAuth();
      
      // Don't redirect if already on auth pages
      const currentPath = window.location.pathname;
      const authPages = ['/login', '/signup', '/auth', '/accept-invite', '/forgot-password', '/reset-password'];
      const isOnAuthPage = authPages.some(page => currentPath.includes(page));
      
      if (!isOnAuthPage) {
        // Store intended destination for redirect after login
        const returnUrl = window.location.pathname + window.location.search;
        if (returnUrl !== '/' && returnUrl !== '/dashboard') {
          sessionStorage.setItem('safer_return_url', returnUrl);
        }
        
        window.location.href = '/login';
      }
    }
    
    // Handle subscription errors
    if (status === 402) {
      // Don't show toast, let component handle redirect
      return Promise.reject(error);
    }
    
    // Handle rate limiting — throttle the toast so a burst of 429s (e.g. rapid
    // navigation hitting the ingress limit) doesn't spam a wall of identical
    // toasts. Show at most one every 5 seconds.
    if (status === 429) {
      const now = Date.now();
      if (now - _last429ToastAt > 5000) {
        _last429ToastAt = now;
        toast.error('Too many requests. Please wait a moment and try again.');
      }
      return Promise.reject(error);
    }
    
    // Handle server errors
    if (status >= 500) {
      toast.error('Server error. Please try again later.');
      return Promise.reject(error);
    }
    
    // Handle other errors — FastAPI HTTPException can have dict `detail`
    const rawDetail = error.response?.data?.detail;
    let message;
    if (typeof rawDetail === 'string') {
      message = rawDetail;
    } else if (rawDetail && typeof rawDetail === 'object') {
      message = rawDetail.message || rawDetail.reason || JSON.stringify(rawDetail);
    } else {
      message = error.message || 'An error occurred';
    }

    // Don't show toast if configured to skip
    if (!error.config?.skipErrorToast) {
      toast.error(message);
    }

    return Promise.reject(error);
  }
);

/**
 * Helper to make requests without error toast OR auth redirect.
 * Used by telemetry and other fire-and-forget signals that may run on
 * public pages where a 401 must not bounce the user to /login.
 */
api.silentRequest = (config) => {
  return api({ ...config, skipErrorToast: true, skipAuthRedirect: true });
};

/**
 * Helper for file uploads with progress
 */
api.uploadFile = (url, formData, onProgress) => {
  return api.post(url, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    onUploadProgress: (progressEvent) => {
      if (onProgress) {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        onProgress(percentCompleted);
      }
    },
  });
};

export default api;
export { API, BACKEND_URL };
